import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getArticles } from '../services/api';
import { Search, ArrowRight, Clock } from 'lucide-react';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import PageHeader from '../components/PageHeader'; // Kept for consistency if needed, or we can build a custom header
import usePageHeaders from '../hooks/usePageHeaders';

// --- Components ---

const SectionTag = ({ children }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className="w-4 h-4 bg-black"></div>
    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-black">
      {children}
    </span>
  </div>
);

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // --- SEO & Schema Markup ---
  useEffect(() => {
    // 1. Title & Meta Tags
    document.title = "Artikel & Wawasan - Blog Konveksi Bandung | iMoonusel";
    
    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta("description", "Dapatkan informasi terbaru seputar industri konveksi, tips memilih bahan seragam, tren fashion, dan wawasan bisnis garment dari ahli iMoonusel.");
    setMeta("keywords", "blog konveksi, tips seragam, bahan kaos, artikel fashion, berita industri garment");

    // Open Graph
    const setOg = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setOg("og:title", "Artikel & Wawasan - Blog Konveksi Bandung | iMoonusel");
    setOg("og:description", "Wawasan mendalam tentang dunia produksi garment dan tekstil.");
    setOg("og:image", "/logo512.png");
    setOg("og:url", window.location.href);
    setOg("og:type", "website");

    // 2. JSON-LD Schema Markup
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "iMoonusel Blog",
      "description": "Artikel seputar industri konveksi dan garment.",
      "url": window.location.href,
      "publisher": {
        "@type": "Organization",
        "name": "iMoonusel Konveksi",
        "logo": {
          "@type": "ImageObject",
          "url": "https://konveksiimoonusel.com/logo512.png"
        }
      }
    };

    const script = document.createElement('script');
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const headers = usePageHeaders();  const navigate = useNavigate();
  const limit = 10; 

  const fetchArticles = useCallback(async (pageNum) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const response = await getArticles(pageNum, limit);
      
      if (response && response.data) {
        setArticles(prev => {
          if (pageNum === 1) return response.data;
          const newIds = new Set(response.data.map(a => a.id));
          return [...prev, ...response.data.filter(a => !newIds.has(a.id))];
        });
        
        if (response.data.length < limit || response.pagination.current_page >= response.pagination.total_pages) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } else {
        setError('Gagal memuat artikel.');
      }
    } catch (err) {
      setError('Terjadi kesalahan saat mengambil data.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles(1);
  }, [fetchArticles]);

  const handleLoadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore && searchQuery === '') {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchArticles(nextPage);
    }
  }, [loading, loadingMore, hasMore, page, fetchArticles, searchQuery]);

  const lastArticleRef = useInfiniteScroll(handleLoadMore, hasMore, loading || loadingMore);

  const getContent = (article, field) => {
    return article[`${field}_id`] || article[`${field}_en`] || '';
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  const filteredArticles = articles.filter(article => {
    const title = getContent(article, 'title').toLowerCase();
    const excerpt = getContent(article, 'excerpt').toLowerCase();
    const query = searchQuery.toLowerCase();
    return title.includes(query) || excerpt.includes(query);
  });

  const isSearching = searchQuery.length > 0;

  // Split Logic
  let heroMain = null;
  let heroSide = [];
  let listStories = [];

  if (isSearching) {
    listStories = filteredArticles;
  } else {
    if (articles.length > 0) {
      heroMain = articles[0];
      heroSide = articles.slice(1, 4); 
      listStories = articles.slice(4); 
    }
  }

  if (loading && articles.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-[10px] font-bold uppercase tracking-widest animate-pulse">Memuat Berita...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black font-sans pt-20">
      
      {/* Editorial Header */}
      <section className="pt-12 pb-12 border-b border-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end gap-6">
            <div>
              <SectionTag>JOURNAL</SectionTag>
              <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-none mb-2">
                WAWASAN <br/>
                INDUSTRI<span className="text-blue-600">.</span>
              </h1>
            </div>
            <div className="w-full md:w-auto">
               <div className="relative border-b border-black">
                  <input 
                    type="text" 
                    placeholder="Cari topik..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full md:w-64 py-2 bg-transparent text-sm font-bold uppercase placeholder-zinc-400 focus:outline-none"
                  />
                  <Search className="absolute right-0 top-2 text-black" size={16} />
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      {!isSearching && heroMain && (
        <section className="border-b border-black">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-12 gap-0">
              
              {/* Main Hero */}
              <div className="md:col-span-8 py-12 md:pr-12 border-b md:border-b-0 md:border-r border-zinc-200 cursor-pointer group" onClick={() => navigate(`/articles/${heroMain.slug}`)}>
                <div className="aspect-[16/9] bg-zinc-100 mb-8 overflow-hidden">
                  <img 
                    src={heroMain.featured_image || '/placeholder.jpg'} 
                    alt="Featured" 
                    className="w-full h-full object-cover transition-all duration-700"
                    onError={(e) => {e.target.style.display='none'}}
                  />
                </div>
                <div className="flex items-center gap-3 mb-4 text-xs font-bold uppercase tracking-widest text-red-600">
                   <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
                   Top Story
                </div>
                <h2 className="text-4xl md:text-5xl font-black tracking-tight leading-[1.1] mb-6 group-hover:text-blue-900 transition-colors">
                  {getContent(heroMain, 'title')}
                </h2>
                <p className="text-lg text-zinc-600 font-serif leading-relaxed line-clamp-3 mb-6">
                  {getContent(heroMain, 'excerpt')}
                </p>
                <div className="flex justify-between items-center border-t border-zinc-100 pt-6">
                   <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">{formatDate(heroMain.created_at)}</span>
                   <span className="text-xs font-bold text-black uppercase tracking-widest flex items-center gap-2 group-hover:gap-4 transition-all">
                     Baca Artikel <ArrowRight size={14} />
                   </span>
                </div>
              </div>

              {/* Side Stories */}
              <div className="md:col-span-4 py-12 md:pl-12 flex flex-col gap-8">
                {heroSide.map((story) => (
                  <div key={story.id} className="cursor-pointer group flex justify-between gap-4 items-start" onClick={() => navigate(`/articles/${story.slug}`)}>
                    <div className="flex-1">
                       <div className="flex gap-2 items-center mb-2">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-blue-600 bg-blue-50 px-2 py-1">Insight</span>
                       </div>
                       <h3 className="text-lg font-bold leading-tight mb-2 group-hover:underline decoration-2 decoration-blue-600 underline-offset-4">
                         {getContent(story, 'title')}
                       </h3>
                       <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{formatDate(story.created_at)}</span>
                    </div>
                    <div className="w-24 h-24 flex-shrink-0 bg-zinc-100 overflow-hidden border border-zinc-100">
                       <img 
                         src={story.featured_image || '/placeholder.jpg'} 
                         alt={getContent(story, 'title')}
                         className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                         onError={(e) => {e.target.style.display='none'}}
                       />
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Main Feed */}
      <section className="py-24 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-12">
             <div className="h-[1px] flex-grow bg-zinc-300"></div>
             <span className="text-xs font-black uppercase tracking-widest text-zinc-400">
               {isSearching ? 'Hasil Pencarian' : 'Arsip Berita'}
             </span>
             <div className="h-[1px] flex-grow bg-zinc-300"></div>
          </div>

          <div className="flex flex-col">
            {listStories.map((article, index) => {
              const isLast = !isSearching && listStories.length === index + 1;
              return (
                <div 
                  key={article.id} 
                  ref={isLast ? lastArticleRef : null}
                  onClick={() => navigate(`/articles/${article.slug}`)}
                  className="group py-12 border-b border-zinc-200 cursor-pointer hover:bg-white transition-colors px-6 -mx-6"
                >
                  <div className="grid md:grid-cols-12 gap-8 items-start">
                     <div className="md:col-span-2 hidden md:block">
                        <span className="text-xs font-bold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                           <Clock size={12} />
                           {formatDate(article.created_at)}
                        </span>
                     </div>
                     <div className="md:col-span-7">
                        <h3 className="text-2xl md:text-3xl font-black tracking-tight mb-4 group-hover:text-blue-800 transition-colors">
                          {getContent(article, 'title')}
                        </h3>
                        <p className="text-zinc-600 font-serif leading-relaxed mb-6">
                          {getContent(article, 'excerpt')}
                        </p>
                        <span className="text-xs font-bold uppercase tracking-widest text-black border-b-2 border-black pb-1 group-hover:text-blue-600 group-hover:border-blue-600 transition-all">
                          Baca Selengkapnya
                        </span>
                     </div>
                     <div className="md:col-span-3 order-first md:order-last mb-4 md:mb-0">
                        <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
                           <img 
                              src={article.featured_image || '/placeholder.jpg'}
                              alt={getContent(article, 'title')}
                              className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                              onError={(e) => {e.target.style.display='none'}}
                           />
                        </div>
                     </div>
                  </div>
                </div>
              );
            })}
          </div>

          {loadingMore && (
             <div className="py-12 text-center">
                <span className="text-[10px] font-bold uppercase tracking-widest animate-pulse">Memuat Lebih Banyak...</span>
             </div>
          )}
          
          {listStories.length === 0 && isSearching && (
             <div className="py-24 text-center text-zinc-400 text-sm font-bold uppercase tracking-widest">
                Tidak ada artikel yang ditemukan.
             </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default Articles;
